<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import ModalHeading from "@/components/global-themed/ui/modals/ModalHeading.vue";
import ModalSubText from "@/components/global-themed/ui/modals/ModalSubText.vue";
import ModalHeader from "@/components/global-themed/ui/modals/ModalHeader.vue";
import KitModalHeader from "@/components/global-themed/ui/modals/KitModalHeader.vue";
import ModalTextAreaWrapper from "@/components/global-themed/ui/modals/ModalTextAreaWrapper.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";
import AlertBox from "@/components/global-themed/AlertBox.vue";
import {computed} from "vue";

const feedbackValue = defineModel({
  type: String,
  required: true,
});

const {revisionErrors: errors, selectedApproval, helpRequested} = useApprovalFlow();

// Only aware based on current session.
const helpAlreadyRequested = computed(() => helpRequested(selectedApproval.value.id))

const emit = defineEmits(['close', 'confirm', 'extra-help']);
</script>

<template>
  <Teleport
    to=".tailwind"
  >
    <BaseModal
      modal-class="tw-w-[800px]"
      @close="emit('close')"
    >
      <ModalContentWrapper>
        <KitModalHeader>
          <KitIcon
            class="tw-w-[88px]"
            state="error"
          />

          <ModalHeader>
            <ModalHeading>Let me fix that for you!</ModalHeading>

            <ModalSubText>
              Tell me what you’d like me to change or improve in the content. The more specific you can be, the better I can refine and personalize it for your business. Once you’ve provided your feedback below, click <span class="tw-font-bold">Continue</span> and I’ll get straight to work!
            </ModalSubText>
          </ModalHeader>
        </KitModalHeader>

        <AlertBox
          v-if="selectedApproval?.revision_count >= 2 && !helpAlreadyRequested"
          type="info"
        >
          <p class="tw-text-inherit">
            It looks like you’ve made a few changes — totally fine! If things still aren’t quite right, I can pass this on to my manager to make sure we’re heading in the right direction.
          </p>
          <DefaultButton
            variant="primary-inverted"
            class="tw-whitespace-nowrap tw-text-sm tw-px-4 tw-py-2.5 tw-my-auto"
            @click="emit('extra-help')"
          >
            Get Extra Help
          </DefaultButton>
        </AlertBox>

        <div class="tw-flex tw-flex-col tw-gap-6">
          <span class="tw-flex tw-gap-3 tw-text-neutral-900 tw-font-bold tw-text-2xl tw-leading-none">
            <FixPanelIcon icon-path="icons/users-talking" />
            What do I need to change?
          </span>

          <ModalTextAreaWrapper :error="!!errors?.feedback">
            <div>
              <textarea
                v-model="feedbackValue"
                placeholder="For Example:

The description is too technical for my audience; please simplify it.

I'd like a more conversational tone"
                rows="8"
                class="tw-w-full tw-text-neutral-900 tw-text-base tw-resize-none"
              />
              <p
                v-if="errors?.feedback"
                class="tw-text-danger"
              >
                {{ errors.feedback[0] }}
              </p>
            </div>
          </ModalTextAreaWrapper>
        </div>

        <div class="tw-flex tw-items-center tw-gap-4 tw-self-end">
          <DefaultButton
            variant="primary-inverted"
            @click="emit('close')"
          >
            Cancel
          </DefaultButton>
          <DefaultButton @click="emit('confirm')">
            Continue
          </DefaultButton>
        </div>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>
</template>
